import { useEffect, useRef, useState } from 'react';
import { compressImage } from '../lib/imageCompress';
import { PhotoAnnotator } from './PhotoAnnotator';

export interface LocalPhoto {
  blob: Blob;
  preview: string;
}

interface Props {
  photos: LocalPhoto[];
  onChange: (next: LocalPhoto[]) => void;
}

export function PhotoAttach({ photos, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [annotating, setAnnotating] = useState<number | null>(null);

  // Revoke object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      photos.forEach((p) => URL.revokeObjectURL(p.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const next: LocalPhoto[] = [];
    for (const f of Array.from(files)) {
      try {
        const blob = await compressImage(f, 1280, 0.85);
        next.push({ blob, preview: URL.createObjectURL(blob) });
      } catch {
        // swallow — keep going
      }
    }
    onChange([...photos, ...next]);
    if (inputRef.current) inputRef.current.value = '';
  }

  function removeAt(i: number) {
    URL.revokeObjectURL(photos[i].preview);
    onChange(photos.filter((_, idx) => idx !== i));
  }

  function onAnnotated(idx: number, annotated: LocalPhoto) {
    const next = photos.slice();
    next[idx] = annotated;
    onChange(next);
    setAnnotating(null);
  }

  return (
    <div className="photo-attach">
      <div className="photo-grid">
        {photos.map((p, i) => (
          <div key={i} className="photo-thumb">
            <button
              type="button"
              className="photo-thumb-tap"
              onClick={() => setAnnotating(i)}
              aria-label={`Annotate photo ${i + 1}`}
            >
              <img src={p.preview} alt={`Photo ${i + 1}`} />
              <span className="photo-thumb-pen" aria-hidden>✎</span>
            </button>
            <button
              type="button"
              className="photo-remove"
              onClick={() => removeAt(i)}
              aria-label="Remove photo"
            >×</button>
          </div>
        ))}
        <label className="photo-add">
          {/* No `capture` attribute — that would force the camera and skip the
              picker. Without it, iOS shows the full options: Photo Library,
              Take Photo, and Choose File (for photos already saved as files
              in iCloud / Dropbox / Files). */}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => onFiles(e.target.files)}
          />
          <span className="plus">+</span>
          <span>Add photo</span>
        </label>
      </div>

      <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
        Take a new photo, or pick one from your library. Tap a photo to annotate it with the red pen.
      </p>

      {annotating !== null && photos[annotating] && (
        <PhotoAnnotator
          photo={photos[annotating]}
          onSave={(a) => onAnnotated(annotating, a)}
          onCancel={() => setAnnotating(null)}
        />
      )}
    </div>
  );
}
